import { useState, useEffect, useRef, useCallback } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Building2, ChevronDown, Check, Crown, Loader2 } from "lucide-react";

const PLAN_DOT = { enterprise: "bg-violet-400", agency: "bg-amber-400", professional: "bg-sky-400", starter: "bg-emerald-400", free: "bg-zinc-500" };

export function BrandSwitcher() {
  const { t } = useLanguage();
  const [brands, setBrands] = useState([]);
  const [currentTid, setCurrentTid] = useState("");
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(null);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/tenants/my-brands");
      setBrands(r.data.brands || []);
      setCurrentTid(r.data.current_tenant_id || "");
    } catch { /* single tenant — ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // Don't render if user has only 1 brand
  if (brands.length <= 1) return null;

  const current = brands.find(b => b.tenant_id === currentTid);

  const handleSwitch = async (tid) => {
    if (tid === currentTid) { setOpen(false); return; }
    setSwitching(tid);
    try {
      const r = await api.post("/tenants/switch", { tenant_id: tid });
      toast.success(`${t("Switched to")} ${r.data.name}`);
      setCurrentTid(tid);
      setOpen(false);
      // Reload page to refresh all data for the new tenant
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Switch failed"));
    }
    setSwitching(null);
  };

  return (
    <div className="relative" ref={ref} data-testid="brand-switcher">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-[var(--app-surface)] transition-colors group"
        data-testid="brand-switcher-trigger">
        <div className="w-6 h-6 rounded-md flex items-center justify-center bg-[var(--app-accent)]/15 flex-shrink-0">
          {current?.logo_url ? (
            <img src={current.logo_url} alt="" className="w-4 h-4 rounded object-contain" />
          ) : (
            <Building2 size={13} className="text-[var(--app-accent)]" />
          )}
        </div>
        <span className="text-xs font-medium text-[var(--app-text)] max-w-[100px] truncate hidden xl:inline">
          {current?.name || "Workspace"}
        </span>
        <ChevronDown size={12} className={`text-[var(--app-text-muted)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1.5 w-56 rounded-xl border border-[var(--app-border)] shadow-2xl z-50 overflow-hidden"
          style={{ backgroundColor: "var(--app-bg)" }} data-testid="brand-switcher-dropdown">
          <div className="px-3 py-2 border-b border-[var(--app-border)]">
            <p className="text-[10px] font-semibold text-[var(--app-text-muted)] uppercase tracking-wider">{t("Your Brands")}</p>
          </div>
          <div className="py-1 max-h-64 overflow-y-auto">
            {brands.map(b => {
              const isActive = b.tenant_id === currentTid;
              const isSwitching = switching === b.tenant_id;
              return (
                <button key={b.tenant_id} onClick={() => handleSwitch(b.tenant_id)}
                  disabled={isSwitching}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${isActive ? "bg-[var(--app-accent)]/10" : "hover:bg-[var(--app-surface-hover)]"}`}
                  data-testid={`brand-option-${b.tenant_id}`}>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[var(--app-surface)] border border-[var(--app-border)] flex-shrink-0">
                    {b.logo_url ? (
                      <img src={b.logo_url} alt="" className="w-5 h-5 rounded object-contain" />
                    ) : (
                      <Building2 size={14} className="text-[var(--app-text-muted)]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-[var(--app-text)] truncate">{b.name}</span>
                      {b.is_primary && <Crown size={10} className="text-amber-400 flex-shrink-0" />}
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${PLAN_DOT[b.plan] || PLAN_DOT.free}`} />
                      <span className="text-[9px] text-[var(--app-text-faint)] capitalize">{b.plan}</span>
                    </div>
                  </div>
                  {isSwitching ? (
                    <Loader2 size={14} className="animate-spin text-[var(--app-accent)] flex-shrink-0" />
                  ) : isActive ? (
                    <Check size={14} className="text-[var(--app-accent)] flex-shrink-0" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
